import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../api/client";
import type { Finding, Me, Survey } from "../api/types";
import { SeverityChip } from "../components/Badges";
import { DataTable } from "../components/DataTable";
import { Dialog, useToast } from "../components/Feedback";
import { Icon } from "../components/Icon";
import { Field, PageHeader, Panel } from "../components/Layout";
import { EmptyState, QueryState, SkeletonBlock } from "../components/States";
import { can } from "../app/auth";
import { chainage, dateTime } from "../lib/format";
import { uniq, useUrlState } from "../lib/useUrlState";

const FILTERS = ["road", "type", "severity", "source"] as const;

function ManualDefectDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const roads = useQuery({ queryKey: ["roads"], queryFn: () => api<{ items: Survey[] }>("/api/roads"), enabled: open });
  const cat = useQuery({ queryKey: ["review", "catalogue"], queryFn: () => api<{ defects: { defect_code: string; defect_name: string }[] }>("/api/review/catalogue"), enabled: open });
  const [survey, setSurvey] = useState("");
  const [code, setCode] = useState("");
  const [severity, setSeverity] = useState("");
  const [ch, setCh] = useState("");
  const [notes, setNotes] = useState("");
  const [error, setError] = useState<string | null>(null);
  const qc = useQueryClient();
  const toast = useToast();
  const nav = useNavigate();
  const m = useMutation({
    mutationFn: () => api<{ id: string }>("/api/defects", { method: "POST", json: { survey_uuid: survey, defect_code: code, severity, chainage_m: Number(ch) * 1000, notes } }),
    onSuccess: (r) => {
      qc.invalidateQueries();
      toast({ text: "Defect added to the register." });
      onClose();
      nav(`/roads/${survey}/findings?f=${encodeURIComponent(r.id)}`);
    },
    onError: (e) => setError(e instanceof Error ? e.message : "The defect could not be added."),
  });
  return (
    <Dialog open={open} onClose={onClose} labelledBy="md-title">
      <form className="dialog-body" onSubmit={(e) => { e.preventDefault(); setError(null); m.mutate(); }}>
        <h2 className="dialog-title" id="md-title">Add a defect</h2>
        <p className="muted">For a defect you saw on site or in the images that the AI missed.</p>
        <Field label="Road" htmlFor="md-road">
          <select id="md-road" className="select" required value={survey} onChange={(e) => setSurvey(e.target.value)}>
            <option value="">Choose a road…</option>
            {roads.data?.items.map((r) => <option key={r.survey_uuid} value={r.survey_uuid}>{r.road_code} — {r.road_name} ({r.survey_date})</option>)}
          </select>
        </Field>
        <div className="form-grid">
          <Field label="Defect type" htmlFor="md-code">
            <select id="md-code" className="select" required value={code} onChange={(e) => setCode(e.target.value)}>
              <option value="">Choose…</option>
              {cat.data?.defects.map((d) => <option key={d.defect_code} value={d.defect_code}>{d.defect_code} {d.defect_name}</option>)}
            </select>
          </Field>
          <Field label="Severity" htmlFor="md-sev">
            <select id="md-sev" className="select" required value={severity} onChange={(e) => setSeverity(e.target.value)}>
              <option value="">Choose…</option>{["Low", "Medium", "High"].map((s) => <option key={s}>{s}</option>)}
            </select>
          </Field>
          <Field label="Chainage (km)" htmlFor="md-ch" hint="For example 12.340">
            <input id="md-ch" className="input" type="number" min="0" step="0.001" required value={ch} onChange={(e) => setCh(e.target.value)} />
          </Field>
        </div>
        <Field label="Notes" htmlFor="md-notes"><textarea id="md-notes" className="textarea" value={notes} onChange={(e) => setNotes(e.target.value)} /></Field>
        {error ? <p className="field-error" role="alert">{error}</p> : null}
        <div className="form-actions">
          <button type="button" className="btn" onClick={onClose}>Cancel</button>
          <button type="submit" className="btn btn-primary" disabled={m.isPending || !survey || !code || !severity || !ch}>Add defect</button>
        </div>
      </form>
    </Dialog>
  );
}

export function DefectsPage({ me }: { me: Me }) {
  const nav = useNavigate();
  const q = useQuery({ queryKey: ["defects"], queryFn: () => api<{ items: Finding[] }>("/api/defects") });
  const f = useUrlState(FILTERS);
  const [adding, setAdding] = useState(false);
  const all = q.data?.items ?? [];
  const opts = useMemo(() => ({ road: uniq(all.map((d) => d.road_code)), type: uniq(all.map((d) => d.type_name)) }), [all]);
  const rows = all.filter((d) =>
    (!f.values.road || d.road_code === f.values.road) && (!f.values.type || d.type_name === f.values.type) &&
    (!f.values.severity || d.severity === f.values.severity) && (!f.values.source || d.source === f.values.source));

  const sel = (k: (typeof FILTERS)[number], label: string, o: { v: string; l: string }[]) => (
    <Field label={label} htmlFor={`df-${k}`}>
      <select id={`df-${k}`} className="select" value={f.values[k]} onChange={(e) => f.set(k, e.target.value)}>
        <option value="">All</option>{o.map((x) => <option key={x.v} value={x.v}>{x.l}</option>)}
      </select>
    </Field>
  );

  return (
    <>
      <PageHeader title="Defects" subtitle="Defects confirmed by engineers, across every road you can see."
        actions={can.review(me.role) ? <button className="btn btn-primary" onClick={() => setAdding(true)}><Icon name="plus" />Add a defect</button> : null} />
      <div className="filters">
        {sel("road", "Road", opts.road.map((v) => ({ v, l: v })))}
        {sel("type", "Defect", opts.type.map((v) => ({ v, l: v })))}
        {sel("severity", "Severity", ["High", "Medium", "Low"].map((v) => ({ v, l: v })))}
        {sel("source", "Source", [{ v: "ai", l: "AI, confirmed" }, { v: "manual", l: "Added by engineer" }, { v: "field", l: "Field observation" }])}
        {f.active ? <button className="btn btn-ghost" onClick={f.clear}>Clear filters</button> : null}
      </div>
      <Panel flush>
        <QueryState query={q} skeleton={<div className="panel-body"><SkeletonBlock rows={8} /></div>}>
          {() => (
            <DataTable<Finding>
              rows={rows}
              rowKey={(r) => r.id}
              onRowClick={(r) => nav(`/roads/${r.survey_uuid}/findings?f=${encodeURIComponent(r.id)}`)}
              initialSort={{ key: "when", dir: "desc" }}
              caption="Verified defects"
              empty={all.length ? <EmptyState title="No defects match these filters" action={<button className="btn" onClick={f.clear}>Clear filters</button>} />
                : <EmptyState title="No verified defects yet" action={can.review(me.role) ? <Link className="btn" to="/review">Open the review queue</Link> : undefined}>Defects appear here once an engineer confirms them.</EmptyState>}
              columns={[
                { key: "type", label: "Defect", sort: (r) => r.type_name, render: (r) => <>{r.type_name}<small className="muted" style={{ display: "block" }}>{r.type_code}</small></> },
                { key: "road", label: "Road", sort: (r) => r.road_code, render: (r) => <Link className="row-link" to={`/roads/${r.survey_uuid}`} onClick={(e) => e.stopPropagation()}>{r.road_code}</Link> },
                { key: "ch", label: "Chainage", sort: (r) => r.chainage_m, render: (r) => <span className="mono">{chainage(r.chainage_m)}</span> },
                { key: "sev", label: "Severity", sort: (r) => ({ High: 3, Medium: 2, Low: 1 })[r.severity ?? ""] ?? 0, render: (r) => <SeverityChip value={r.severity} /> },
                { key: "qty", label: "Quantity", align: "right", render: (r) => (r.quantity ? `${r.quantity} ${r.unit ?? ""}` : "—") },
                { key: "tr", label: "Treatment", render: (r) => r.treatment || "—" },
                { key: "by", label: "Verified by", sort: (r) => r.reviewed_by, render: (r) => r.reviewed_by || "—" },
                { key: "when", label: "Date", sort: (r) => r.reviewed_at, render: (r) => dateTime(r.reviewed_at) },
              ]}
            />
          )}
        </QueryState>
      </Panel>
      <ManualDefectDialog open={adding} onClose={() => setAdding(false)} />
    </>
  );
}
